import { useRef, useEffect, useState } from 'react';
import './CameraComponent.scss';

interface CameraComponentProps {
  isCameraActive: boolean;
  onDeactivateCamera: () => void;
  triggerAnalysis: (file: File) => void;
}

const CameraComponent: React.FC<CameraComponentProps> = ({
  isCameraActive,
  onDeactivateCamera,
  triggerAnalysis,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isLandscape, setIsLandscape] = useState<boolean>(
    window.innerWidth > window.innerHeight,
  );
  const isAndroid = /Android/i.test(navigator.userAgent);

  const enterFullscreenMode = () => {
    if (isAndroid) {
      // Solicitar pantalla completa
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if ((document.documentElement as any).webkitRequestFullscreen) {
        (document.documentElement as any).webkitRequestFullscreen();
      } else if ((document.documentElement as any).msRequestFullscreen) {
        (document.documentElement as any).msRequestFullscreen();
      }

      // Agregar clase específica para Android
      document.body.classList.add('android-fullscreen-camera');

      // Intentar ocultar la barra de navegación del sistema en Android
      if (window.navigator && (navigator as any).standalone !== undefined) {
        // Para dispositivos más nuevos, intentar usar la API de pantalla completa inmersiva
        const element = document.documentElement;
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if ((element as any).webkitRequestFullscreen) {
          (element as any).webkitRequestFullscreen();
        }
      }
    }
  };

  const exitFullscreenMode = () => {
    if (isAndroid) {
      // Quitar clase específica para Android
      document.body.classList.remove('android-fullscreen-camera');

      // Salir del modo pantalla completa
      if (document.exitFullscreen) {
        document
          .exitFullscreen()
          .catch((e) => console.log('Error al salir de pantalla completa:', e));
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      setTimeout(() => {
        const newIsLandscape = window.innerWidth > window.innerHeight;
        setIsLandscape(newIsLandscape);

        // Volver a entrar en modo fullscreen después de la rotación
        // pero sin bloquear la orientación
        if (isCameraActive && isAndroid) {
          enterFullscreenMode();
        }
      }, 300); // Aumentamos el tiempo para dar más margen a la rotación
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [isCameraActive]);

  useEffect(() => {
    if (isCameraActive) {
      startCamera();
      setTimeout(enterFullscreenMode, 100);
    } else {
      stopCamera();
      exitFullscreenMode();
    }

    return () => {
      stopCamera();
      exitFullscreenMode();
    };
  }, [isCameraActive]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (error) {
      console.error('Error al acceder a la cámara:', error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
  };

  const handleDeactivateCamera = () => {
    stopCamera(); // Detener la cámara
    onDeactivateCamera(); // Llamar a la función proporcionada por las props
  };

  const capturePhoto = () => {
    if (!canvasRef.current || !videoRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const isPortrait = video.videoHeight > video.videoWidth;

    // Usar la resolución nativa del video para máxima calidad
    const maxWidth = isAndroid ? 4096 : 1920; // Valores más altos para Android
    const maxHeight = isAndroid ? 2160 : 1080;

    // Calcular dimensiones manteniendo la relación de aspecto
    let captureWidth, captureHeight;

    if (isPortrait) {
      captureWidth = Math.min(video.videoHeight, maxHeight);
      captureHeight = Math.min(video.videoWidth, maxWidth);
    } else {
      captureWidth = Math.min(video.videoWidth, maxWidth);
      captureHeight = Math.min(video.videoHeight, maxHeight);
    }

    canvas.width = captureWidth;
    canvas.height = captureHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    // Limpiar el canvas antes de dibujar
    context.clearRect(0, 0, canvas.width, canvas.height);

    if (isPortrait) {
      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate(-90 * (Math.PI / 180));
      context.drawImage(
        video,
        -video.videoWidth / 2,
        -video.videoHeight / 2,
        video.videoWidth,
        video.videoHeight,
      );
    } else {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    // Genera un blob con la máxima calidad para Android
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // Convierte el blob en un objeto File
          const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });

          console.log(
            `Foto capturada: ${canvas.width}x${
              canvas.height
            }, tamaño: ${Math.round(blob.size / 1024)}KB`,
          );

          // Ejecuta triggerAnalysis con el archivo generado
          triggerAnalysis(file);

          // Cerrar la cámara después de capturar la imagen
          handleDeactivateCamera();
        }
      },
      'image/jpeg',
      isAndroid ? 1.0 : 0.8, // Calidad máxima para Android
    );
  };

  if (!isCameraActive) {
    return null; // No se muestra nada si la cámara está inactiva
  }

  return (
    <div className={`camera-fullscreen ${isAndroid ? 'android' : 'ios'}`}>
      <div className="camera-preview">
        <video
          ref={videoRef}
          className={`video ${isAndroid ? 'android' : 'ios'}`}
          autoPlay
          playsInline
        />
        <div className="focus-frame"></div>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
      <div className="controls">
        <button className="capture-button" onClick={capturePhoto}>
          Capturar
        </button>
        <button className="back-button" onClick={handleDeactivateCamera}>
          Volver
        </button>
      </div>
      {!isLandscape && (
        <div className="orientation-alert">
          <div className="alert-backdrop" />
          <div className="alert-message">
            <p>
              Por favor, coloque su dispositivo en modo horizontal para tomar la
              foto.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraComponent;
